import { FormControl, FormLabel, Switch } from "@chakra-ui/react";
import DynamicForm from "@components/DynamicForm/DynamicForm";
import { UIFormField } from "@components/DynamicForm/types";
import DocumentMonoIcon from "@icons/DocumentMonoIcon";
import { FormFieldType } from "@models/form";
import { useUserStore } from "@providers/auth";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { usePaymentStore } from "../../../payment";
import CheckoutSectionWrapper from "./CheckoutSectionWrapper";

const _t = (key: string) => key;

const DEFAULT_BILLING_FORM_FIELDS: UIFormField[] = [
  {
    type: FormFieldType.TEXT,
    label: {
      en: "Full Name",
    },
    key: "name",
    required: true,
  },
  {
    type: FormFieldType.EMAIL,
    label: {
      en: "Email Address",
    },
    key: "email",
    required: true,
    span: 1,
  },
];

const INVOICE_FORM_FIELDS: UIFormField[] = [
  {
    type: FormFieldType.TEXT,
    label: {
      en: "Company Name",
    },
    key: "billingCompany",
    span: 2,
  },
  {
    type: FormFieldType.TEXT,
    label: {
      en: "Billing Address",
    },
    key: "billingAddress",
    span: 2,
  },
  {
    type: FormFieldType.TEXT,
    label: {
      en: "VAT Number",
    },
    key: "vatNumber",
    span: 2,
  },
];

export const BILLING_FORM_FIELDS: UIFormField[] = [
  ...DEFAULT_BILLING_FORM_FIELDS,
  ...INVOICE_FORM_FIELDS,
];

const CheckoutBillingForm = ({
  onChange: onChange = () => {},
  onValidChange: onValidChange = () => {},
}: {
  onChange: (values: FieldValues) => void;
  onValidChange: (valid: boolean) => void;
}) => {
  const user = useUserStore((state) => state.user);
  const transaction = usePaymentStore((state) => state.transaction);
  const [initialized, setInitialized] = useState(false);
  const [invoice, setInvoice] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors, isValid, isDirty },
    setValue,
    getValues,
    watch,
    trigger,
  } = useForm({
    mode: "onChange",
  });

  useEffect(() => {
    onValidChange(isValid);
  }, [isValid]);

  useEffect(() => {
    const subscription = watch(async () => {
      trigger();
      onChange(getValues());
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // prefill contact info if available
  useEffect(() => {
    if (!transaction || !user || initialized) return;
    setInitialized(true);
    const email = transaction.contact.email || user.email || "";
    setValue("email", email);
    const name =
      transaction.items.find((item) => item.contact.email === email)?.contact
        .name ||
      transaction.contact.name ||
      "";
    setValue("name", name);
  }, [
    user?.email,
    transaction?.contact.email,
    transaction?.contact.name,
    initialized,
  ]);

  const fields = useMemo(() => {
    return invoice ? BILLING_FORM_FIELDS : DEFAULT_BILLING_FORM_FIELDS;
  }, [invoice]);

  const RequestInvoiceToggle = () => {
    return (
      <FormControl
        display="flex"
        flexDirection="row"
        alignItems="center"
        w="auto"
        className="sm:px-3"
      >
        <FormLabel
          fontSize={{ base: "sm", sm: "sm" }}
          htmlFor="request-invoice"
        >
          {_t("Request Invoice")}
        </FormLabel>
        <Switch
          id="request-invoice"
          colorScheme="primary"
          size="sm"
          isChecked={invoice}
          onChange={(e) => {
            setInvoice(!invoice);
          }}
        />
      </FormControl>
    );
  };

  return (
    <CheckoutSectionWrapper
      isDirty={isDirty}
      isValid={isValid}
      title={_t("Billing Information")}
      icon={DocumentMonoIcon}
    >
      <RequestInvoiceToggle />
      <DynamicForm
        fields={fields}
        errors={errors}
        register={register}
        handleSubmit={handleSubmit}
        isDirty={isDirty}
      />
    </CheckoutSectionWrapper>
  );
};

export default CheckoutBillingForm;
